<template>
  <div class="add-to-cart-fixed bg-white fixed bottom-0 left-0 right-0 c-p-2 flex">
    <div class="container flex items-center">
      <div class="c-flex-grow-1 flex items-center">
        <div class="c-md:flex hidden c-md:col-3 c-lg:col-2">
          <RemoteResponsiveImage
            :product-image-data="sortedProductImages[0].data"
            :img-class="`embed-responsive-item relative w-100 ${
              sortedProductImages[0].has_padding ? 'image-contain' : 'image-cover'
            }`"
            :alt="sortedProductImages[0].alt || sortedProductImages[0].label"
            sizes="(min-width: 768px) 25vw, (min-width: 600px) 35vw, (min-width: 500px) 50vw, 70vw"
            :lazy-load="false"
          />
        </div>
        <div class="col-12 c-md:col-9 c-lg:col-10">
          <span class="c-md:block hidden c-mb-2 c-md:c-mb-1">
            {{ product.name }}
          </span>
          <div class="block">
            <strong class="text-lg-c c-mb-0 c-mr-1 font-family-base">
              <span
                :class="{
                  'line-through text-danger': product.plusClubPrice !== null && getCurrentCustomer?.inPlusClub,
                }"
              >
                <ProductPrice
                  :price-first="product.price"
                  :currency="product.currency"
                />
              </span>
              <span v-if="product.weight > 0 || product.volue > 0"> /</span>
            </strong>
            <span
              v-if="product.weight > 0 || product.volume > 0"
              class="text-lg-c c-m-0 font-family-base font-normal text-muted"
            >{{ product.unit === 'g' ? product.weight : product.volume }}&nbsp;{{ product.unit }}</span>
          </div>
          <p class="c-mb-0">
            <strong
              v-if="product.hasDiscount && (product.plusClubPrice === null || !getCurrentCustomer?.inPlusClub)"
              class="line-through text-danger text-sm-c c-mb-0 c-mr-3 c-lg:c-mr-4 font-family-base"
            >
              <ProductPrice
                :price-first="product.priceWithoutDiscount"
                :currency="product.currency"
              />
            </strong>
            <span
              v-if="product.hasDiscount && product.plusClubPrice !== null && !product.price"
              class="text-sm-c font-family-base whitespace-nowrap text-muted"
            >
              / {{ product.weight }}&nbsp;g
            </span>
          </p>
        </div>
      </div>
      <div
        v-if="product.isReadyProduct || product.isGift"
        class="col-auto"
      >
        <AddToCartButton
          v-if="product.state === 'available'"
          :show-decrement-button="true"
          :product="product"
          :wide="true"
          theme="transparent"
          :odometer-fill="true"
        />
        <ButtonState
          v-else
          :state="product.state"
          :product="product"
          added-class="w-100"
        />
      </div>
      <div
        v-else-if="product.isIngredient"
        class="col c-lg:col-auto"
      >
        <PushToMixButton
          v-if="product.state === 'available'"
          :product="product"
          added-class="w-100"
        />
        <ButtonState
          v-else
          :state="product.state"
          :product="product"
          added-class="w-100"
        />
      </div>
      <div class="col-auto c-md:flex hidden c-m-0 relative z-2">
        <Favorite
          v-if="!product.isIngredient"
          :id="product.id"
          box-class="c-m-0"
          btn-class="btn  btn-info stretched-link c-rounded-pill border-0 c-p-2 relative"
          icon-size="28"
          icon-color-class="text-white"
        />
        <ButtonShare
          v-if="!isHiddenProduct"
          :link="productShowLink"
          class="col-auto"
          :icon-only="true"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'
import { useAppStore } from '@/stores/AppStore'

const RemoteResponsiveImage = defineAsyncComponent(() => import('@/components/general/BaseRemoteResponsiveImage.vue'))
const AddToCartButton = defineAsyncComponent(() => import('@/components/buttons/ButtonAddToCartButton.vue'))
const PushToMixButton = defineAsyncComponent(() => import('@/components/buttons/ButtonPushToMixButton.vue'))
const ButtonShare = defineAsyncComponent(() => import('@/components/buttons/ButtonShare.vue'))
const ButtonState = defineAsyncComponent(() => import('@/components/buttons/ButtonState.vue'))
const Favorite = defineAsyncComponent(() => import('@/components/buttons/ButtonFavorite.vue'))
const ProductPrice = defineAsyncComponent(() => import('@/components/products/ProductPrice.vue'))

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  productVariants: {
    type: Object,
    required: true,
  },
  isHiddenProduct: {
    type: Boolean,
    required: true,
  },
})

const showAddToCart = () => {
  const staticAddToCartBtn = document.querySelector('.product-buttons')
  const addToCartBtn = document.querySelector('.add-to-cart-fixed')
  if (addToCartBtn && staticAddToCartBtn) {
    if (staticAddToCartBtn.getBoundingClientRect().top < 0) addToCartBtn.classList.add('active')
    else addToCartBtn.classList.remove('active')
  }
}

window.addEventListener('scroll', async () => {
  showAddToCart()
})
const appStore = useAppStore()
const { getCurrentCustomer } = storeToRefs(appStore)

const sortedProductImages = computed(() => {
  return [...props.product.images].sort((a, b) => Number(b.main_image) - Number(a.main_image))
})

const route = useRoute()
const baseUrl = import.meta.env.VITE_VUE_APP_ORIGINAL_URL

const productShowLink = computed(() => {
  return (baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl) + route.fullPath
})

</script>

<style lang="scss" scoped>
header {
  min-height: 80px;

  .product-breadcrumb {
    min-height: 40px;
  }
}

.product-carousel__wrapper {
  min-height: 86vw;
  height: 100%;

  @media screen and (min-width: 575px) {
    min-height: 340px;
  }

  @media screen and (min-width: 992px) {
    min-height: 500px;
  }

  @media screen and (min-width: 1200px) {
    min-height: 562px;
  }
}

.product-buttons {
  @media screen and (max-width: 1199px) {
    width: 100%;
  }
}

.to-comments {
  min-height: 24px;
}

.to-description {
  bottom: -4px;

  &:before {
    content: '';
    position: absolute;
    width: 180%;
    height: 100%;
    bottom: 0;
    right: 0;
    background-image: linear-gradient(to left, rgba(247, 231, 209, 1) 60%, rgba(247, 231, 209, 0));
  }

  span {
    z-index: 2;
    position: relative;
  }
}

.product-variants {
  height: 48px;
}

.product-description-text {
  height: 100px;
  overflow: hidden;

  @media screen and (max-width: 991px) {
    display: none;
  }
}
.mixit-plus-svg {
  width: 86px;
}
.mixit-plus-flex {
  @media screen and (max-width: 500px) {
    flex-basis: 100%;
  }
}
.line-through {
  text-decoration: line-through;
}
</style>

<i18n>
  {
    "cs": {
    "for_mixit_plus": "pro členy",
    "ingredient": "Namixuj si",
    "main_page": "Hlavní strana",
    "pack_discount_tooltip": "Sleva je vypočítána jako rozdíl oproti nákupu obsažených produktů samostatně",
    "ratings": {
        "few": "hodnocení",
        "one": "hodnocení",
        "other": "hodnocení"
    },
    "read_more": "Číst více",
    "selected_chocolate_products": "Jsem u ledu! Z důvodu vysokých teplot mě poslali do chládku.",
    "voucher_permalink": "mixit-plus-na-12-mesicu"
},
    "sk": {
    "for_mixit_plus": "pre členov",
    "ingredient": "Namixuj si",
    "main_page": "Hlavná stránka",
    "pack_discount_tooltip": "Zľava sa počíta ako rozdiel oproti samostatnému nákupu zahrnutých produktov",
    "ratings": {
        "few": "hodnotenie",
        "one": "hodnotenie",
        "other": "hodnotenie"
    },
    "read_more": "Prečítať si viac",
    "selected_chocolate_products": "Som v chládku! Čokoláda má kvôli vysokým teplotám dovolenku.",
    "voucher_permalink": "mixit-plus-na-12-mesiacov"
},
    "pl": {
    "for_mixit_plus": "dla członków",
    "ingredient": "Skomponuj",
    "main_page": "Strona główna",
    "pack_discount_tooltip": "Zniżka jest obliczana jako różnica w porównaniu do zakupu poszczególnych produktów osobno.\n\n\n\n\n\n\n\n",
    "ratings": {
        "few": "ocena",
        "one": "ocena",
        "other": "ocena"
    },
    "read_more": "Czytaj więcej",
    "selected_chocolate_products": "Czekam, na niższą temperaturę! Ze względu na upały, czekolada została wysłana na przymusowy urlop.\n\n",
    "voucher_permalink": "mixit-plus-na-12-miesiecy"
},
    "hu": {
    "for_mixit_plus": "tagoknak",
    "ingredient": "Mixelj",
    "main_page": "Főoldal",
    "pack_discount_tooltip": "A kedvezmény a termékek külön-külön történő megvásárlásához képest számított különbségként kerül kiszámításra.",
    "ratings": {
        "few": "értékelés",
        "one": "értékelés",
        "other": "értékelés"
    },
    "read_more": "Tovább",
    "selected_chocolate_products": "Hidegre helyezve! :( A magas hőmérsékletek miatt a csokoládés termékek szabadságon vannak.",
    "voucher_permalink": "mixit-plusz-12-honapra"
},
    "de": {
    "for_mixit_plus": "für Mitglieder",
    "ingredient": "Mix dir",
    "main_page": "Startseite",
    "pack_discount_tooltip": "Der Rabatt entspricht der Differenz gegenüber dem Einzelkauf der enthaltenen Produkte.",
    "ratings": {
        "few": "Bewertung",
        "one": "Bewertung",
        "other": "Bewertung"
    },
    "read_more": "Mehr lesen",
    "selected_chocolate_products": "Dieses Produkt ist bei warmen Temperaturen vorübergehend nicht erhältlich.",
    "voucher_permalink": "mixit-plus-fuer-12-monate"
},
    "at": {
    "for_mixit_plus": "für Mitglieder",
    "ingredient": "Mix dir",
    "main_page": "Startseite",
    "pack_discount_tooltip": "Der Rabatt entspricht der Differenz gegenüber dem Einzelkauf der enthaltenen Produkte.",
    "ratings": {
        "few": "Bewertung",
        "one": "Bewertung",
        "other": "Bewertung"
    },
    "read_more": "Mehr lesen",
    "selected_chocolate_products": "Dieses Produkt ist bei warmen Temperaturen vorübergehend nicht erhältlich.",
    "voucher_permalink": "mixit-plus-fuer-12-monate"
},
    "ro": {
    "for_mixit_plus": "pentru membri",
    "ingredient": "Creează propriul mix",
    "main_page": "Pagina principală",
    "pack_discount_tooltip": "Reducerea este calculată ca diferența față de achiziționarea separată a produselor incluse.",
    "ratings": {
        "few": "evaluare",
        "one": "evaluare",
        "other": "evaluare"
    },
    "read_more": "Citiți mai mult",
    "selected_chocolate_products": "La rece! :( Din cauza temperaturilor înalte produsele cu ciocolată sunt în concediu.",
    "voucher_permalink": "mixit-plus-de-12-luni"
},
    "fr": {
    "for_mixit_plus": "pour les membres",
    "ingredient": "Crée ton mix",
    "main_page": "Page principale",
    "pack_discount_tooltip": "La remise est calculée comme la différence par rapport à l’achat des produits inclus séparément",
    "ratings": {
        "few": "avis des clients",
        "one": "avis des clients",
        "other": "avis des clients"
    },
    "read_more": "En savoir plus",
    "selected_chocolate_products": "Je suis au frais ! À cause des températures élevées, ils m'ont plongée dans le froid.",
    "voucher_permalink": "mixit-plus-pour-12 mois"
},
    "it": {
    "for_mixit_plus": "per i membri",
    "ingredient": "Mescola",
    "main_page": "Pagina principale",
    "pack_discount_tooltip": "Lo sconto è calcolato come differenza rispetto all'acquisto dei singoli prodotti separatamente.",
    "ratings": {
        "few": "valutazione",
        "one": "valutazione",
        "other": "valutazione"
    },
    "read_more": "Leggi di più",
    "selected_chocolate_products": "Sono sotto ghiaccio! A causa delle alte temperature mi hanno mandato in spedizione polare per raffreddarmi.",
    "voucher_permalink": "mixit-plus-per-12-mesi"
}
  }
</i18n>
